import React, { useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import { WidgetScreen } from "../../common/WidgetScreen";

import { WidgetFile } from "widgets/src/WidgetFile/WidgetFile";

import { WAFileSettings } from "./WAFileSettings";
import { WSFileNavigationProp, WSFileRouteProp } from "./WSFileStack";
import { WSFileUpdateMutation } from "./__generated__/WSFileUpdateMutation.graphql";
import { WSFileQuery } from "./__generated__/WSFileQuery.graphql";

import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { WidgetScreenPreview } from "../WidgetScreenPreview/WidgetScreenPreview";

type Props = {
  navigation: WSFileNavigationProp<"WSFileMain">;
  route: WSFileRouteProp<"WSFileMain">;
};

function InnerWSFileMain(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<WSFileQuery>(
    graphql`
      query WSFileQuery($id: ID!, $files: [FileInput!]) {
        widgetFile(id: $id) {
          id
          height
          slideshowDuration
          preview(files: $files) {
            ...WidgetFile_widgetFile
            files {
              file {
                id
              }
            }
          }
        }
      }
    `,
    {
      id: props.route.params.widgetId,
      files: props.route.params.fileIds?.map((fileId) => ({ fileId })) ?? null,
    },
    { fetchPolicy: "store-and-network", fetchKey: props.fetchKey }
  );

  const widget = data.widgetFile;

  const slideshowDuration =
    props.route.params.slideshowDuration ?? widget?.slideshowDuration ?? 15;

  const [onUpdateWidget, loading] = useMutation<WSFileUpdateMutation>(graphql`
    mutation WSFileUpdateMutation(
      $widgetId: ID!
      $update: WidgetFileUpdateInput!
    ) {
      updateWidgetFile(id: $widgetId, update: $update) {
        id
      }
    }
  `);

  const onSave = useCallback(() => {
    onUpdateWidget({
      variables: {
        widgetId: props.route.params.widgetId,
        update: {
          setSlideshowDuration: props.route.params.slideshowDuration,
          setFiles: props.route.params.fileIds?.map((fileId) => ({
            fileId,
          })),
        },
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  }, [
    onUpdateWidget,
    props.route.params.widgetId,
    props.route.params.slideshowDuration,
    props.route.params.fileIds,
    props.navigation,
  ]);

  const fileIds = useMemo(
    () =>
      props.route.params.fileIds ??
      widget?.preview?.files?.map((f) => f?.file?.id) ??
      [],
    [props.route.params.fileIds, widget?.preview?.files]
  );

  return (
    <WidgetScreen
      settings={
        <>
          <WAFileSettings
            slideshowDuration={slideshowDuration}
            setSlideShowDuration={(duration) => {
              props.navigation.setParams({
                slideshowDuration: duration,
              });
            }}
            onRemoveFile={(removeId) => {
              props.navigation.setParams({
                fileIds: fileIds.filter((f) => f !== removeId),
              });
            }}
            onAddFile={() => {
              if (!props.route.params.isOrg) {
                props.navigation.navigateDeprecated("WSFileSelect", {
                  ...props.route.params,
                  fileIds,
                });
              } else {
                props.navigation.navigateDeprecated("WSWorkspaceFilesSelect", {
                  ...props.route.params,
                  fileIds,
                  workspaceId: props.route.params.workspaceId,
                });
              }
            }}
            fileIds={fileIds}
            onSetOrder={(order) => {
              props.navigation.setParams({
                fileIds: order,
              });
            }}
          />
        </>
      }
      preview={
        widget?.preview ? (
          <WidgetScreenPreview
            onSave={onSave}
            loading={loading}
            size={{
              w: 2,
              h: widget.height as WidgetSizeT["h"],
            }}
            unitSize={props.route.params.unitSize}
          >
            <WidgetFile widget={widget.preview} idiom="TOUCH" mode="IDLE" />
          </WidgetScreenPreview>
        ) : null
      }
    />
  );
}

export function WSFileMain(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchkey) => <InnerWSFileMain {...props} fetchKey={fetchkey} />}
    </LazyQueryNetworkBoundrary>
  );
}
